import { api } from "@/lib/axios";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { 
    EmployeeCodeParams,
    EmployeeCodeResponse,
    EmployeeCode,
} from "@/interface/standard/st_employee_code";
import { toast } from "sonner";

export const useEmployeeCode = (params: EmployeeCodeParams) => {
    const { page = 0, size = 20, search, sort, isActive, exactMatch } = params;

    return useQuery<EmployeeCodeResponse>({
        queryKey: ["employeeCode", page, size, search, sort, isActive, exactMatch],
        queryFn: async () => {
            const response = await api.get("/employee-code", {
                params: {
                    page,
                    size,
                    name: search,
                    sort,
                    isActive,
                    exactMatch,
                },
            });
            return response.data;
        },
        staleTime: 1000 * 60, // 1분간 캐시 유지
        refetchOnMount: true,
        refetchOnWindowFocus: false,
    });
};

export const useEmployeeCodeSearch = () => {
    return useQuery({
        queryKey: ["employeeCodeSearch"],
        queryFn: () => api.get("/employee-code/search"),
        enabled: true,
    });
};

export const useEmployeeCodeResetPassword = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (name: string) => {
            const response = await api.put(`/employee-code/reset-password-by-name/${encodeURIComponent(name)}`);
            return response.data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["employeeCode"] });
            toast.success("비밀번호가 초기화되었습니다.");
        },
        onError: () => {
            toast.error("비밀번호 초기화에 실패했습니다.");
        },
    });
};

export const useEmployeeCodeInsert = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (data: Partial<EmployeeCode>) => {
            const response = await api.post("/employee-code", data);
            return response.data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["employeeCode"] });
            toast.success("사원이 추가되었습니다.");
        },
        onError: (error: any) => {
            console.error("사원 추가 실패:", error);
        },
    });
};

export const useEmployeeCodeDelete = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (name: string) => {
            const response = await api.delete(`/employee-code/by-name/${encodeURIComponent(name)}`);
            return response.data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["employeeCode"] });
            toast.success("사원이 삭제되었습니다.");
        },
        onError: (error: any) => {
            toast.error(error?.response?.data?.message || "사원 삭제에 실패했습니다.");
        },
    });
};

export const useUpdateEmployeeCode = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async ({ name, data }: { name: string; data: Partial<EmployeeCode> }) => {
            const response = await api.put(`/employee-code/${encodeURIComponent(name)}`, data);
            return response.data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["employeeCode"] });
            toast.success("사원 정보가 수정되었습니다.");
        },
        onError: (error: any) => {
            toast.error(error?.response?.data?.message || "사원 정보 수정에 실패했습니다.");
        },
    });
};